import * as React from 'react';
import { observer } from 'mobx-react';
import { observable } from 'mobx';
import { resolve } from 'inversify-react';
import autobind from 'autobind-decorator';
import { connect } from "react-redux";
import { Dispatch } from 'redux';

import { ToastNotifications } from 'src/core/ui/toast';
import { TourItineraryDateListView } from './tour-itinerary-date-list.view';
import { ContextService } from 'src/core/services/context.service';
import { ObjectUtils } from 'src/core/utils/object';
import { TourItineraryDateDataService } from 'src/app/products/tour-itineraries/tour-itinerary-date/tour-itinerary-date.data';
import { actionCreators } from "src/store/products/product-form-nav-actions";

interface PropsFromState {
	dates: TourItineraryDateItem[];
}

interface PropsFromDispatch {
	navigateToDate: (itinerary: TourItineraryDateItem, title: string, text: string, key: string) => any;
	datesLoaded: (itinerary: TourItineraryItem, dates: TourItineraryDateItem[]) => any;
}

interface Props extends ComponentViewProps<TourItineraryItem> {
	contextService: ContextService;
	mode: ControlMode;
	languageCode: string;
}

@observer
class TourItineraryDateListController extends React.Component<Props & PropsFromState & PropsFromDispatch, {}> {
	@resolve
	private toast: ToastNotifications;

	@observable
	private dataService: TourItineraryDateDataService;

	componentDidMount(): void {
		this.loadData();
	}

	private async loadData(): Promise<void> {
		const { contextService, model, dates, datesLoaded } = this.props;
		this.dataService = TourItineraryDateDataService.make(contextService);

		if (dates == null && model.id) {
			this.dataService.addFilter('tourItineraryId', 'eq', model.id);
			this.dataService.addFilter('isDeleted', 'eq', false);
			this.dataService.addSorting('id', 'asc', true);
			this.dataService.pageSize = 25;
			try {
				this.props.setBusy(true);
				const items = await this.dataService.load(query => query);
				datesLoaded(model, items.slice());
			} catch (error) {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			} finally {
				this.props.setBusy(false);
			}
		} else {
			this.dataService.items = dates;
		}
	}

	@autobind
	private async handleItemDoubleClick(item?: TourItineraryDateItem): Promise<void> {
		const { model, languageCode, navigateToDate, datesLoaded } = this.props;
		const errors = await model.validate(false);
		if (errors.length > 0) {
			this.toast.showToast('Validation error!', errors.map(o => o.text), 'error');
			return;
		}

		if (item == null) {
			item = await this.dataService.add(model);
			datesLoaded(model, [item]);
		}
		const displayName = item.dynamicProperties.displayName[languageCode] || 'New Itinerary Date';
		navigateToDate(item, 'Itinerary Date', displayName, 'itinerary-date');
	}

	render(): React.ReactNode {
		if (this.dataService == null || this.dataService.items == null) {
			return null;
		}

		return (
			<TourItineraryDateListView
				model={this.dataService}
				onCreateItem={this.handleItemDoubleClick}
				onEditItem={this.handleItemDoubleClick}
				languageCode={this.props.languageCode}
				mode={this.props.mode}
				setBusy={this.props.setBusy}
			/>
		);
	}
}

const mapStateToProps = (
	state: IApplicationState,
	props: Props,
): PropsFromState => {
	const itinerary = state.otherLoadedEntities.find(e => e.entity == props.model.entity) as TourItineraryItem;
	return {
		dates: itinerary.dates,
	};
};

const mapDispatchToProps = (dispatch: Dispatch): PropsFromDispatch => ({
	navigateToDate: (
		entity,
		title: string,
		text: string,
		key: string
	) => dispatch(actionCreators.productFormUpNav(entity, title, text, key)),
	datesLoaded: (
		itinerary: TourItineraryItem,
		dates: TourItineraryDateItem[],
	) => dispatch(actionCreators.productFormRelationshipLoaded(itinerary, dates, "dates")),
});

const connected = connect(mapStateToProps, mapDispatchToProps)(TourItineraryDateListController);
export { connected as TourItineraryDateListController };



// WEBPACK FOOTER //
// ./src/app/products/tour-itineraries/tour-itinerary-date/list/tour-itinerary-date-list.controller.tsx